<template>
  <div class="campanhas-aniversario-container">
    <!-- Header com Breadcrumb -->
    <div class="page-header mb-3">
      <div class="d-flex align-items-center justify-content-between">
        <div class="flex-grow-1">
          <b-breadcrumb class="breadcrumb-custom mb-2">
            <b-breadcrumb-item :to="{ name: 'campaign-ia' }">
              <feather-icon
                icon="ZapIcon"
                size="16"
              />
              Campanhas Inteligentes
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              Campanhas de Aniversário
            </b-breadcrumb-item>
          </b-breadcrumb>
          <h2 class="page-title mb-1">
            <feather-icon
              icon="GiftIcon"
              size="28"
              class="text-primary mr-2"
            />
            Campanhas de Aniversário
          </h2>
          <p class="text-muted mb-0">
            Envie mensagens automáticas para clientes no dia do aniversário (sem recomendações de produtos)
          </p>
        </div>
      </div>
    </div>

    <!-- Informação da Loja Selecionada -->
    <b-alert
      v-if="lojaSelecionada"
      variant="info"
      show
      class="loja-info mb-4"
    >
      <div class="d-flex align-items-center">
        <feather-icon
          icon="StoreIcon"
          size="18"
          class="mr-2"
        />
        <div>
          <strong>Loja Selecionada:</strong> {{ nomeLojaSelecionada }}
        </div>
      </div>
    </b-alert>

    <!-- Loading -->
    <div
      v-if="carregandoStatus"
      class="text-center py-5"
    >
      <b-spinner variant="primary" />
      <p class="mt-2 text-muted">
        Carregando informações...
      </p>
    </div>

    <!-- Conteúdo Principal -->
    <template v-else>
      <!-- Campanha Ativa -->
      <div
        v-if="campanhaAtiva"
        class="campanha-ativa-section"
      >
        <b-card class="campanha-card shadow-sm mb-4">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div class="d-flex align-items-center">
              <div class="status-badge-active mr-3">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="24"
                />
              </div>
              <div>
                <h4 class="mb-0">
                  {{ campanhaAtiva.name }}
                </h4>
                <small class="text-success">
                  <feather-icon
                    icon="ActivityIcon"
                    size="14"
                  />
                  Campanha Ativa
                </small>
              </div>
            </div>
            <b-button
              variant="danger"
              size="sm"
              :disabled="processando"
              @click="confirmarDesativacao"
            >
              <b-spinner
                v-if="processando"
                small
                class="mr-1"
              />
              <feather-icon
                v-else
                icon="PauseIcon"
                size="14"
                class="mr-50"
              />
              Desativar
            </b-button>
          </div>

          <!-- Configurações da Campanha -->
          <b-row class="config-info">
            <b-col
              md="3"
              class="mb-3"
            >
              <div class="info-item">
                <feather-icon
                  icon="ShoppingBagIcon"
                  size="18"
                  class="text-primary mr-2"
                />
                <div>
                  <small class="text-muted d-block">Template 1 (Recentes)</small>
                  <strong>{{ campanhaAtiva.template_name_recent || campanhaAtiva.template_name }}</strong>
                </div>
              </div>
            </b-col>
            <b-col
              md="3"
              class="mb-3"
            >
              <div class="info-item">
                <feather-icon
                  icon="UsersIcon"
                  size="18"
                  class="text-primary mr-2"
                />
                <div>
                  <small class="text-muted d-block">Template 2 (Inativos)</small>
                  <strong>{{ campanhaAtiva.template_name_inactive || campanhaAtiva.template_name }}</strong>
                </div>
              </div>
            </b-col>
            <b-col
              md="3"
              class="mb-3"
            >
              <div class="info-item">
                <feather-icon
                  icon="CalendarIcon"
                  size="18"
                  class="text-primary mr-2"
                />
                <div>
                  <small class="text-muted d-block">Período de Compra</small>
                  <strong>{{ campanhaAtiva.recent_purchase_days || campanhaAtiva.purchase_days || 15 }} dias</strong>
                </div>
              </div>
            </b-col>
            <b-col
              md="3"
              class="mb-3"
            >
              <div class="info-item">
                <feather-icon
                  icon="ClockIcon"
                  size="18"
                  class="text-primary mr-2"
                />
                <div>
                  <small class="text-muted d-block">Horário de Envio</small>
                  <strong>{{ formatarHorario(campanhaAtiva.send_time) }}</strong>
                </div>
              </div>
            </b-col>
            <b-col
              md="3"
              class="mb-3"
            >
              <div class="info-item">
                <feather-icon
                  icon="TrendingUpIcon"
                  size="18"
                  class="text-primary mr-2"
                />
                <div>
                  <small class="text-muted d-block">Limite Diário</small>
                  <strong>{{ campanhaAtiva.daily_limit }} mensagens</strong>
                </div>
              </div>
            </b-col>
            <b-col
              md="3"
              class="mb-3"
            >
              <div class="info-item">
                <feather-icon
                  icon="FileTextIcon"
                  size="18"
                  class="text-primary mr-2"
                />
                <div>
                  <small class="text-muted d-block">Templates Configurados</small>
                  <strong>2 templates</strong>
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- Botão Ver Métricas -->
          <div class="mt-3 text-center">
            <b-button
              variant="primary"
              :to="{ name: 'campaign-ia-aniversario-metricas', query: { loja: lojaSelecionada, nomeLoja: nomeLojaSelecionada } }"
            >
              <feather-icon
                icon="BarChart2Icon"
                size="16"
                class="mr-50"
              />
              Ver Métricas Completas
            </b-button>
          </div>
        </b-card>
      </div>

      <div
        v-else
        class="configuracao-section"
      >
        <b-card class="config-card shadow-sm">
          <div class="text-center mb-4">
            <div class="empty-icon mb-3">
              <feather-icon
                icon="GiftIcon"
                size="64"
                class="text-muted"
              />
            </div>
            <h4>Configure sua Campanha de Aniversário</h4>
            <p class="text-muted">
              Envie mensagens automáticas para clientes no dia do aniversário
            </p>
          </div>

          <b-form @submit.prevent="salvarCampanha">
            <b-form-group
              label="Nome da Campanha"
              label-class="font-weight-bold"
            >
              <b-form-input
                v-model="form.name"
                placeholder="Ex: Campanha de Aniversário 2026"
                required
              />
            </b-form-group>

            <!-- Campo para definir dias -->
            <b-form-group
              label-class="font-weight-bold"
              class="mb-4"
            >
              <template #label>
                Período de Compra (em dias)
                <feather-icon
                  v-b-tooltip.hover.top="'Define o período para separar clientes recentes (que compraram nos últimos X dias) dos inativos (que compraram há mais de X dias)'"
                  icon="HelpCircleIcon"
                  size="16"
                  class="text-muted ml-1 cursor-pointer"
                />
              </template>
              <b-form-input
                v-model.number="form.purchase_days"
                type="number"
                min="1"
                max="365"
                placeholder="Ex: 15"
                required
              />
              <small class="text-muted">
                Os clientes serão automaticamente classificados em recentes ou inativos baseado neste período
              </small>
            </b-form-group>

            <b-form-group
              label="Template 1 - Compradores Recentes"
              label-class="font-weight-bold"
            >
              <small class="text-muted d-block mb-2">
                <feather-icon
                  icon="ShoppingBagIcon"
                  size="14"
                  class="mr-1"
                />
                Para clientes que <strong>compraram nos últimos {{ form.purchase_days }} dias</strong>
              </small>
              <div class="template-selector">
                <div
                  v-if="!templateSelecionado1"
                  class="template-empty"
                  @click="abrirModalTemplates('template1')"
                >
                  <feather-icon
                    icon="FileTextIcon"
                    size="32"
                    class="text-muted mb-2"
                  />
                  <p class="mb-1">
                    <strong>Selecionar Template 1</strong>
                  </p>
                  <small class="text-muted">Clique para escolher um template aprovado</small>
                </div>
                <div
                  v-else
                  class="template-selected"
                >
                  <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                      <feather-icon
                        icon="CheckCircleIcon"
                        size="20"
                        class="text-success mr-2"
                      />
                      <div>
                        <strong>{{ templateSelecionado1.name }}</strong>
                        <br>
                        <small class="text-muted">Template para compradores recentes</small>
                      </div>
                    </div>
                    <div>
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        @click="abrirModalTemplates('template1')"
                      >
                        <feather-icon
                          icon="RefreshCwIcon"
                          size="14"
                          class="mr-50"
                        />
                        Trocar
                      </b-button>
                      <b-button
                        variant="outline-danger"
                        size="sm"
                        class="ml-1"
                        @click="removerTemplate('template1')"
                      >
                        <feather-icon
                          icon="TrashIcon"
                          size="14"
                        />
                      </b-button>
                    </div>
                  </div>
                </div>
              </div>
            </b-form-group>

            <!-- Upload de mídia para Template 1 -->
            <b-form-group
              v-if="template1TemMidia"
              label="Mídia do Template 1"
              label-class="font-weight-bold"
              description="Este template tem cabeçalho com imagem ou vídeo."
              :state="validacaoMidia1"
            >
              <div
                v-if="previewImagem1 || previewVideo1"
                class="media-preview mb-2"
              >
                <img
                  v-if="previewImagem1 && !previewVideo1"
                  :src="previewImagem1"
                  alt="Preview"
                  class="img-preview rounded"
                >
                <video
                  v-else-if="previewVideo1"
                  controls
                  class="video-preview rounded"
                >
                  <source
                    :src="previewVideo1"
                    type="video/mp4"
                  >
                </video>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  class="mt-2"
                  @click="removerMidia('template1')"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="14"
                    class="mr-50"
                  />
                  Remover mídia
                </b-button>
              </div>
              <div
                v-else
                class="upload-area p-4 rounded border"
                @click="$refs.fileInputMidia1.click()"
              >
                <feather-icon
                  icon="UploadCloudIcon"
                  size="40"
                  class="text-muted mb-2"
                />
                <p class="mb-1 text-muted">
                  Clique para selecionar imagem ou vídeo
                </p>
                <small class="text-muted">Imagem: JPG, PNG, GIF – máx. 5MB · Vídeo: MP4 – máx. 16MB</small>
              </div>
              <input
                ref="fileInputMidia1"
                type="file"
                accept="image/*,video/mp4,video/quicktime,video/x-msvideo"
                class="d-none"
                @change="handleMediaUpload($event, 'template1')"
              >
              <b-form-invalid-feedback :state="validacaoMidia1">
                É necessário fazer upload da mídia para o Template 1
              </b-form-invalid-feedback>
            </b-form-group>

            <hr class="my-4">

            <b-form-group
              label="Template 2 - Compradores Antigos"
              label-class="font-weight-bold"
            >
              <small class="text-muted d-block mb-2">
                <feather-icon
                  icon="UsersIcon"
                  size="14"
                  class="mr-1"
                />
                Para clientes que <strong>compraram há mais de {{ form.purchase_days }} dias</strong>
              </small>
              <div class="template-selector">
                <div
                  v-if="!templateSelecionado2"
                  class="template-empty"
                  @click="abrirModalTemplates('template2')"
                >
                  <feather-icon
                    icon="FileTextIcon"
                    size="32"
                    class="text-muted mb-2"
                  />
                  <p class="mb-1">
                    <strong>Selecionar Template 2</strong>
                  </p>
                  <small class="text-muted">Clique para escolher um template aprovado</small>
                </div>
                <div
                  v-else
                  class="template-selected"
                >
                  <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                      <feather-icon
                        icon="CheckCircleIcon"
                        size="20"
                        class="text-success mr-2"
                      />
                      <div>
                        <strong>{{ templateSelecionado2.name }}</strong>
                        <br>
                        <small class="text-muted">Template para compradores antigos</small>
                      </div>
                    </div>
                    <div>
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        @click="abrirModalTemplates('template2')"
                      >
                        <feather-icon
                          icon="RefreshCwIcon"
                          size="14"
                          class="mr-50"
                        />
                        Trocar
                      </b-button>
                      <b-button
                        variant="outline-danger"
                        size="sm"
                        class="ml-1"
                        @click="removerTemplate('template2')"
                      >
                        <feather-icon
                          icon="TrashIcon"
                          size="14"
                        />
                      </b-button>
                    </div>
                  </div>
                </div>
              </div>
            </b-form-group>

            <!-- Upload de mídia para Template 2 -->
            <b-form-group
              v-if="template2TemMidia"
              label="Mídia do Template 2"
              label-class="font-weight-bold"
              description="Este template tem cabeçalho com imagem ou vídeo."
              :state="validacaoMidia2"
            >
              <div
                v-if="previewImagem2 || previewVideo2"
                class="media-preview mb-2"
              >
                <img
                  v-if="previewImagem2 && !previewVideo2"
                  :src="previewImagem2"
                  alt="Preview"
                  class="img-preview rounded"
                >
                <video
                  v-else-if="previewVideo2"
                  controls
                  class="video-preview rounded"
                >
                  <source
                    :src="previewVideo2"
                    type="video/mp4"
                  >
                </video>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  class="mt-2"
                  @click="removerMidia('template2')"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="14"
                    class="mr-50"
                  />
                  Remover mídia
                </b-button>
              </div>
              <div
                v-else
                class="upload-area p-4 rounded border"
                @click="$refs.fileInputMidia2.click()"
              >
                <feather-icon
                  icon="UploadCloudIcon"
                  size="40"
                  class="text-muted mb-2"
                />
                <p class="mb-1 text-muted">
                  Clique para selecionar imagem ou vídeo
                </p>
                <small class="text-muted">Imagem: JPG, PNG, GIF – máx. 5MB · Vídeo: MP4 – máx. 16MB</small>
              </div>
              <input
                ref="fileInputMidia2"
                type="file"
                accept="image/*,video/mp4,video/quicktime,video/x-msvideo"
                class="d-none"
                @change="handleMediaUpload($event, 'template2')"
              >
              <b-form-invalid-feedback :state="validacaoMidia2">
                É necessário fazer upload da mídia para o Template 2
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
              label="Horário de Envio Diário"
              label-class="font-weight-bold"
            >
              <b-form-input
                v-model="form.birthday_send_time"
                type="time"
                required
              />
              <small class="text-muted">
                Horário em que as mensagens serão enviadas todos os dias
              </small>
            </b-form-group>

            <b-form-group
              label="Limite Diário de Envios"
              label-class="font-weight-bold"
            >
              <b-form-input
                v-model.number="form.birthday_daily_limit"
                type="number"
                min="1"
                max="10000"
                placeholder="Ex: 1000"
                required
              />
              <small class="text-muted">
                Máximo de mensagens que podem ser enviadas por dia (1-10.000)
              </small>
            </b-form-group>

            <b-alert
              variant="info"
              show
              class="info-box mt-4"
            >
              <div class="d-flex">
                <feather-icon
                  icon="InfoIcon"
                  size="20"
                  class="mr-2"
                />
                <div>
                  <strong>Como funciona:</strong>
                  <ul class="mb-0 mt-2">
                    <li>Todo dia à meia-noite, o sistema verifica automaticamente os aniversariantes do dia</li>
                    <li>Para cada aniversariante, o sistema verifica se houve compra nos últimos {{ form.purchase_days }} dias</li>
                    <li><strong>Se comprou:</strong> Recebe o Template 1</li>
                    <li><strong>Se não comprou:</strong> Recebe o Template 2</li>
                    <li>Cada cliente recebe apenas 1 mensagem por ano</li>
                    <li>O limite diário evita envios descontrolados</li>
                  </ul>
                </div>
              </div>
            </b-alert>

            <div class="d-flex justify-content-between mt-4">
              <b-button
                variant="outline-secondary"
                @click="voltarParaLista"
              >
                <feather-icon
                  icon="ArrowLeftIcon"
                  size="16"
                  class="mr-50"
                />
                Voltar
              </b-button>
              <b-button
                type="submit"
                variant="primary"
                :disabled="processando || !formularioValido"
              >
                <b-spinner
                  v-if="processando"
                  small
                  class="mr-1"
                />
                <feather-icon
                  v-else
                  icon="CheckIcon"
                  size="16"
                  class="mr-50"
                />
                Criar e Ativar Campanha
              </b-button>
            </div>
          </b-form>
        </b-card>
      </div>
    </template>

    <!-- Modal de Seleção de Templates -->
    <modal-selecao-templates
      v-model="modalTemplateVisivel"
      :templates="templatesDisponiveis"
      :carregando="false"
      @template-selecionado="onTemplateSelecionado"
    >
      <template #info-text>
        <strong>Apenas templates aprovados</strong> pelo WhatsApp estão disponíveis para uso em campanhas.
      </template>
      <template #empty-text>
        Crie templates e aguarde a aprovação do WhatsApp para utilizá-los em campanhas.
      </template>
    </modal-selecao-templates>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BBreadcrumb,
  BBreadcrumbItem,
  BButton,
  BCard,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BModal,
  BPagination,
  BRow,
  BSpinner,
  BTable,
  VBTooltip,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import ModalSelecaoTemplates from '@core/components/modal-selecao-templates/ModalSelecaoTemplates.vue'
import { listarTemplates, buscaChatsTeste } from '@/functions/basics/chat'
import {
  criarCampanhaAniversario,
  desativarCampanhaAniversario,
  buscarStatusCampanhaAniversario,
  buscarCampanhaAniversarioAtiva,
  uploadMediaCampanhaAniversario,
  buscarMetricasConversaoAniversario,
  buscarPedidosAniversario,
  buscarMensagensEnviadasAniversario,
  buscarInfoClienteAniversario,
} from '@/functions/basics/birthday-campaigns'

export default {
  name: 'CampanhasAniversario',
  components: {
    BAlert,
    BBadge,
    BBreadcrumb,
    BBreadcrumbItem,
    BButton,
    BCard,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BModal,
    BPagination,
    BRow,
    BSpinner,
    BTable,
    FeatherIcon,
    ModalSelecaoTemplates,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      lojaSelecionada: null,
      nomeLojaSelecionada: '',
      campanhaAtiva: null,
      metricas: null,
      mostrarMetricas: false,
      carregandoStatus: false,
      processando: false,
      carregandoTemplates: false,
      // Conversões
      janelaAtribuicao: 168,
      opcoesJanelaAtribuicao: [
        { value: 24, text: '24h (1 dia)' },
        { value: 48, text: '48h (2 dias)' },
        { value: 72, text: '72h (3 dias)' },
        { value: 120, text: '120h (5 dias)' },
        { value: 168, text: '168h (7 dias)' },
      ],
      metricasConversao: null,
      carregandoConversoes: false,
      // Modal de Pedidos
      modalPedidosVisivel: false,
      pedidosConversao: [],
      carregandoPedidos: false,
      currentPage: 1,
      perPage: 10,
      camposPedidos: [
        { key: 'created_at', label: 'Data', sortable: true },
        { key: 'order_id', label: 'Pedido', sortable: true },
        { key: 'customer_name', label: 'Cliente', sortable: true },
        { key: 'order_value', label: 'Valor', sortable: true },
        { key: 'time_to_conversion', label: 'Tempo até Conversão', sortable: true },
      ],
      // Modal de Envios
      modalEnviosVisivel: false,
      mensagensEnviadas: [],
      carregandoEnvios: false,
      currentPageEnvios: 1,
      perPageEnvios: 10,
      filtroEnvios: '',
      filtroStatusEnvio: 'all',
      opcoesStatusEnvio: [
        { value: 'all', text: 'Todos' },
        { value: 'sent', text: 'Enviados' },
        { value: 'scheduled', text: 'Agendados' },
        { value: 'errors', text: 'Erros (Bloqueados, Falhas, Cancelados)' },
        { value: 'blocked', text: '→ Bloqueados' },
        { value: 'failed', text: '→ Falhas' },
        { value: 'cancelled', text: '→ Cancelados' },
      ],
      camposEnvios: [
        { key: 'sent_at', label: 'Data Envio', sortable: true },
        { key: 'customer_name', label: 'Cliente', sortable: true },
        { key: 'template_type', label: 'Tipo', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'acoes', label: 'Ações', sortable: false },
      ],
      // Modal de Informações do Cliente
      modalClienteVisivel: false,
      clienteSelecionado: null,
      carregandoCliente: false,
      // Templates (dois templates diferentes baseados em condição de compra)
      chatSelecionado: null,
      modalTemplateVisivel: false,
      templatesDisponiveis: [],
      templateAtualSendoSelecionado: null, // 'template1' ou 'template2'
      templateSelecionado1: null, // Template para compradores recentes
      templateSelecionado2: null, // Template para compradores inativos
      previewImagem1: null,
      previewVideo1: null,
      imageFile1: null,
      previewImagem2: null,
      previewVideo2: null,
      imageFile2: null,
      form: {
        name: 'Campanha de Aniversário',
        template_name_recent: null, // Nome do template para compradores recentes
        template_name_inactive: null, // Nome do template para compradores inativos
        birthday_send_time: '10:00',
        birthday_daily_limit: 1000,
        purchase_days: 15, // Número de dias para definir cliente recente
      },
    }
  },
  computed: {
    template1TemMidia() {
      const templateObj = this.templateSelecionado1?.template
      if (!templateObj?.components) return false
      const header = templateObj.components.find(c => c.type === 'HEADER')
      return header && ['IMAGE', 'VIDEO'].includes(header.format)
    },
    template2TemMidia() {
      const templateObj = this.templateSelecionado2?.template
      if (!templateObj?.components) return false
      const header = templateObj.components.find(c => c.type === 'HEADER')
      return header && ['IMAGE', 'VIDEO'].includes(header.format)
    },
    validacaoMidia1() {
      if (!this.template1TemMidia) return null
      return !!(this.imageFile1 || this.previewImagem1 || this.previewVideo1)
    },
    validacaoMidia2() {
      if (!this.template2TemMidia) return null
      return !!(this.imageFile2 || this.previewImagem2 || this.previewVideo2)
    },
    formularioValido() {
      const base = (
        this.form.name
        && this.templateSelecionado1
        && this.templateSelecionado2
        && this.form.birthday_send_time
        && this.form.birthday_daily_limit >= 1
        && this.form.birthday_daily_limit <= 10000
        && this.form.purchase_days >= 1
        && this.form.purchase_days <= 365
      )
      if (!base) return false
      if (this.template1TemMidia && !this.validacaoMidia1) return false
      if (this.template2TemMidia && !this.validacaoMidia2) return false
      return true
    },
    mensagensEnviadasFiltradas() {
      if (!this.mensagensEnviadas) return []

      let filtradas = [...this.mensagensEnviadas]

      // Filtro por status
      if (this.filtroStatusEnvio !== 'all') {
        if (this.filtroStatusEnvio === 'errors') {
          // Agrupa blocked, failed e cancelled em "errors"
          filtradas = filtradas.filter(m => ['blocked', 'failed', 'cancelled'].includes(m.status))
        } else {
          filtradas = filtradas.filter(m => m.status === this.filtroStatusEnvio)
        }
      }

      // Filtro por texto (nome ou telefone)
      if (this.filtroEnvios) {
        const termo = this.filtroEnvios.toLowerCase()
        filtradas = filtradas.filter(m => (m.customer_name && m.customer_name.toLowerCase().includes(termo))
          || (m.customer_phone && m.customer_phone.toLowerCase().includes(termo)))
      }

      return filtradas
    },
    totalErros() {
      if (!this.metricas) return 0
      const blocked = this.metricas.total_blocked || 0
      const failed = this.metricas.total_failed || 0
      const cancelled = this.metricas.total_cancelled || 0
      return blocked + failed + cancelled
    },
  },
  async created() {
    this.lojaSelecionada = this.$route.query.loja || localStorage.getItem('lojaSelecionada')
    this.nomeLojaSelecionada = this.$route.query.nomeLoja || localStorage.getItem('nomeLojaSelecionada') || 'Loja'

    if (!this.lojaSelecionada) {
      this.$bvToast.toast('Selecione uma loja primeiro', {
        title: 'Loja Obrigatória',
        variant: 'warning',
        solid: true,
      })
      this.$router.push({ name: 'campaign-ia' })
      return
    }

    await this.buscarChatDisponivel()
    await this.verificarCampanhaAtiva()

    // Se veio do botão "Ver Métricas", mostrar métricas automaticamente
    if (this.$route.query.verMetricas === 'true' && this.metricas) {
      this.mostrarMetricas = true
    }
  },
  methods: {
    async buscarChatDisponivel() {
      try {
        const contas = await buscaChatsTeste()
        if (contas.chats && contas.chats.length > 0) {
          this.chatSelecionado = contas.chats[0].chat_token
        }
      } catch (error) {
        console.error('Erro ao buscar chats:', error)
      }
    },

    async abrirModalTemplates(tipoTemplate) {
      try {
        if (!this.chatSelecionado) {
          this.$bvToast.toast('Nenhuma conta do WhatsApp encontrada.', {
            title: 'Erro',
            variant: 'warning',
            solid: true,
          })
          return
        }

        this.templateAtualSendoSelecionado = tipoTemplate
        const templatesDisponiveis = await listarTemplates(this.chatSelecionado)
        this.templatesDisponiveis = templatesDisponiveis.templates || []
        this.modalTemplateVisivel = true
      } catch (error) {
        console.error('Erro ao carregar templates:', error)
        this.$bvToast.toast('Erro ao carregar templates do WhatsApp', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },

    onTemplateSelecionado(templateData) {
      if (this.templateAtualSendoSelecionado === 'template1') {
        this.templateSelecionado1 = templateData
        this.form.template_name_recent = templateData.name
        this.removerMidia('template1')

        // 🆕 Tentar pré-carregar mídia do template automaticamente
        this.preCarregarMidiaDoTemplate(templateData, 'template1')

        this.$bvToast.toast(`Template 1 "${templateData.name}" selecionado!`, {
          title: 'Template para Compradores Recentes',
          variant: 'success',
          solid: true,
        })
      } else if (this.templateAtualSendoSelecionado === 'template2') {
        this.templateSelecionado2 = templateData
        this.form.template_name_inactive = templateData.name
        this.removerMidia('template2')

        // 🆕 Tentar pré-carregar mídia do template automaticamente
        this.preCarregarMidiaDoTemplate(templateData, 'template2')

        this.$bvToast.toast(`Template 2 "${templateData.name}" selecionado!`, {
          title: 'Template para Compradores Inativos',
          variant: 'success',
          solid: true,
        })
      }
    },

    removerTemplate(tipoTemplate) {
      if (tipoTemplate === 'template1') {
        this.templateSelecionado1 = null
        this.form.template_name_recent = null
        this.removerMidia('template1')
        this.$bvToast.toast('Template 1 removido!', {
          title: 'Removido',
          variant: 'info',
          solid: true,
        })
      } else if (tipoTemplate === 'template2') {
        this.templateSelecionado2 = null
        this.form.template_name_inactive = null
        this.removerMidia('template2')
        this.$bvToast.toast('Template 2 removido!', {
          title: 'Removido',
          variant: 'info',
          solid: true,
        })
      }
    },

    handleMediaUpload(event, tipoTemplate) {
      const file = event.target.files?.[0]
      if (!file) return
      const isImage = file.type.startsWith('image/')
      const isVideo = file.type.startsWith('video/')
      if (!isImage && !isVideo) {
        this.$bvToast.toast('Selecione uma imagem (JPG, PNG, GIF) ou vídeo (MP4, AVI)', {
          title: 'Formato inválido',
          variant: 'warning',
          solid: true,
        })
        return
      }
      const maxSize = isImage ? 5 * 1024 * 1024 : 16 * 1024 * 1024
      if (file.size > maxSize) {
        this.$bvToast.toast(isImage ? 'Imagem deve ter no máximo 5MB' : 'Vídeo deve ter no máximo 16MB', {
          title: 'Arquivo grande',
          variant: 'warning',
          solid: true,
        })
        return
      }

      if (tipoTemplate === 'template1') {
        this.imageFile1 = file
        const reader = new FileReader()
        reader.onload = e => {
          if (isImage) {
            this.previewImagem1 = e.target.result
            this.previewVideo1 = null
          } else {
            this.previewVideo1 = e.target.result
            this.previewImagem1 = null
          }
        }
        reader.readAsDataURL(file)
      } else if (tipoTemplate === 'template2') {
        this.imageFile2 = file
        const reader = new FileReader()
        reader.onload = e => {
          if (isImage) {
            this.previewImagem2 = e.target.result
            this.previewVideo2 = null
          } else {
            this.previewVideo2 = e.target.result
            this.previewImagem2 = null
          }
        }
        reader.readAsDataURL(file)
      }

      this.$bvToast.toast(isImage ? 'Imagem selecionada' : 'Vídeo selecionado', {
        title: 'Mídia',
        variant: 'success',
        solid: true,
      })
    },

    removerMidia(tipoTemplate) {
      if (tipoTemplate === 'template1') {
        this.previewImagem1 = null
        this.previewVideo1 = null
        this.imageFile1 = null
        if (this.$refs.fileInputMidia1) {
          this.$refs.fileInputMidia1.value = ''
        }
      } else if (tipoTemplate === 'template2') {
        this.previewImagem2 = null
        this.previewVideo2 = null
        this.imageFile2 = null
        if (this.$refs.fileInputMidia2) {
          this.$refs.fileInputMidia2.value = ''
        }
      }
    },

    // 🆕 Método para pré-carregar mídia do template automaticamente (igual campanhas segmentadas)
    async preCarregarMidiaDoTemplate(templateData, tipoTemplate) {
      if (!templateData.template?.components) {
        console.log('❌ Template sem components')
        return
      }

      const components = Array.isArray(templateData.template.components)
        ? templateData.template.components
        : Object.values(templateData.template.components)

      const headerComponent = components.find(c => c.type === 'HEADER')

      if (!headerComponent) {
        console.log('✅ Template sem HEADER - não precisa de mídia')
        return
      }

      if (headerComponent.format !== 'IMAGE' && headerComponent.format !== 'VIDEO') {
        console.log(`✅ Template com HEADER tipo ${headerComponent.format} - não precisa de mídia`)
        return
      }

      // Tentar baixar a mídia de exemplo do template
      const exampleHandle = headerComponent.example?.header_handle?.[0]

      if (!exampleHandle) {
        console.log('⚠️ Template tem header de mídia mas não tem exemplo (header_handle)')
        return
      }

      console.log(`📥 Baixando ${headerComponent.format} de exemplo do template...`, exampleHandle)

      try {
        // Baixar a mídia via proxy no backend
        const response = await fetch(exampleHandle)

        if (!response.ok) {
          console.error('❌ Erro ao baixar mídia:', response.status)
          return
        }

        const blob = await response.blob()

        // Determinar extensão baseada no formato
        const extension = headerComponent.format === 'VIDEO' ? 'mp4' : 'jpg'
        const mimeType = headerComponent.format === 'VIDEO' ? 'video/mp4' : 'image/jpeg'

        // Criar File a partir do Blob
        const file = new File(
          [blob],
          `template-media-${Date.now()}.${extension}`,
          { type: mimeType },
        )

        // Criar preview
        const reader = new FileReader()
        reader.onload = e => {
          if (tipoTemplate === 'template1') {
            if (headerComponent.format === 'VIDEO') {
              this.previewVideo1 = e.target.result
              this.previewImagem1 = null
            } else {
              this.previewImagem1 = e.target.result
              this.previewVideo1 = null
            }
            this.imageFile1 = file
          } else if (tipoTemplate === 'template2') {
            if (headerComponent.format === 'VIDEO') {
              this.previewVideo2 = e.target.result
              this.previewImagem2 = null
            } else {
              this.previewImagem2 = e.target.result
              this.previewVideo2 = null
            }
            this.imageFile2 = file
          }

          console.log(`✅ ${headerComponent.format} do template carregado automaticamente`)

          this.$bvToast.toast(
            `${headerComponent.format === 'VIDEO' ? 'Vídeo' : 'Imagem'} do template carregado automaticamente`,
            {
              title: 'Mídia Pré-carregada',
              variant: 'info',
              solid: true,
            },
          )
        }
        reader.readAsDataURL(file)
      } catch (error) {
        console.error('❌ Erro ao pré-carregar mídia do template:', error)
      }
    },

    async verificarCampanhaAtiva() {
      this.carregandoStatus = true
      try {
        const campanha = await buscarCampanhaAniversarioAtiva(this.lojaSelecionada)

        if (campanha) {
          this.campanhaAtiva = campanha
          await this.carregarMetricas(campanha.id)
        }
      } catch (error) {
        console.error('Erro ao verificar campanha ativa:', error)
      } finally {
        this.carregandoStatus = false
      }
    },

    async carregarMetricas(campaignId) {
      try {
        const status = await buscarStatusCampanhaAniversario(campaignId, this.lojaSelecionada)
        this.metricas = status.metrics || null

        // Carregar métricas de conversão também
        await this.carregarMetricasConversao()
      } catch (error) {
        console.error('Erro ao carregar métricas:', error)
      }
    },

    async carregarMetricasConversao() {
      if (!this.campanhaAtiva) return

      this.carregandoConversoes = true
      try {
        const conversoes = await buscarMetricasConversaoAniversario(
          this.campanhaAtiva.id,
          this.lojaSelecionada,
          this.janelaAtribuicao,
        )
        this.metricasConversao = conversoes
      } catch (error) {
        console.error('Erro ao carregar métricas de conversão:', error)
        this.metricasConversao = {
          total_conversoes: 0,
          taxa_conversao: 0,
          valor_total: 0,
          ticket_medio: 0,
        }
      } finally {
        this.carregandoConversoes = false
      }
    },

    async abrirModalPedidos() {
      this.modalPedidosVisivel = true
      this.carregandoPedidos = true
      this.currentPage = 1

      try {
        const pedidos = await buscarPedidosAniversario(
          this.campanhaAtiva.id,
          this.lojaSelecionada,
          this.janelaAtribuicao,
        )
        this.pedidosConversao = pedidos || []
      } catch (error) {
        console.error('Erro ao carregar pedidos:', error)
        this.$bvToast.toast('Erro ao carregar pedidos da campanha', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        this.pedidosConversao = []
      } finally {
        this.carregandoPedidos = false
      }
    },

    fecharModalPedidos() {
      this.modalPedidosVisivel = false
      this.pedidosConversao = []
      this.currentPage = 1
    },

    baixarCSVPedidos() {
      if (!this.pedidosConversao || this.pedidosConversao.length === 0) {
        this.$bvToast.toast('Nenhum pedido para exportar', {
          title: 'Aviso',
          variant: 'warning',
          solid: true,
        })
        return
      }

      try {
        const headers = [
          'Data',
          'ID do Pedido',
          'Cliente',
          'Telefone',
          'Valor',
          'Tempo até Conversão (min)',
        ]

        const rows = this.pedidosConversao.map(pedido => [
          this.formatarDataCompleta(pedido.created_at),
          pedido.order_id || 'N/A',
          pedido.customer_name || 'N/A',
          pedido.customer_phone || 'N/A',
          pedido.order_value || '0',
          pedido.time_to_conversion || '0',
        ])

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
        ].join('\n')

        const BOM = '\uFEFF'
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })

        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        const nomeCampanha = this.campanhaAtiva?.name || 'campanha-aniversario'
        const timestamp = new Date().toISOString().split('T')[0]
        const nomeArquivo = `pedidos_${nomeCampanha}_${timestamp}.csv`

        link.setAttribute('href', url)
        link.setAttribute('download', nomeArquivo)
        link.style.visibility = 'hidden'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        URL.revokeObjectURL(url)

        this.$bvToast.toast('CSV baixado com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error('Erro ao gerar CSV:', error)
        this.$bvToast.toast('Erro ao gerar arquivo CSV', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },

    formatarMoeda(valor) {
      if (valor === null || valor === undefined) return 'R$ 0,00'
      const numero = typeof valor === 'string' ? parseFloat(valor) : valor
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(numero)
    },

    formatarDataCompleta(data) {
      if (!data) return 'N/A'
      return new Date(data).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    formatarTempoConversao(minutos) {
      if (!minutos || minutos === 0) return 'Imediato'

      const horas = Math.floor(minutos / 60)
      const mins = Math.floor(minutos % 60)

      if (horas > 0) {
        return `${horas}h ${mins}min`
      }

      return `${mins}min`
    },

    formatarDataAniversario(data) {
      if (!data) return 'Não informado'
      const date = new Date(data)
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
      })
    },

    getStatusVariant(status) {
      const variants = {
        scheduled: 'info',
        sent: 'success',
        failed: 'danger',
        blocked: 'warning',
        cancelled: 'secondary',
      }
      return variants[status] || 'secondary'
    },

    getStatusLabel(status) {
      const labels = {
        scheduled: 'Agendado',
        sent: 'Enviado',
        failed: 'Falha',
        blocked: 'Bloqueado',
        cancelled: 'Cancelado',
      }
      return labels[status] || status
    },

    async abrirModalEnvios() {
      await this.abrirModalEnviosComFiltro('all')
    },

    async abrirModalEnviosComFiltro(status = 'all') {
      this.modalEnviosVisivel = true
      this.carregandoEnvios = true
      this.currentPageEnvios = 1
      this.filtroEnvios = ''
      this.filtroStatusEnvio = status

      try {
        const mensagens = await buscarMensagensEnviadasAniversario(
          this.campanhaAtiva.id,
          this.lojaSelecionada,
        )
        this.mensagensEnviadas = mensagens || []
      } catch (error) {
        console.error('Erro ao carregar mensagens enviadas:', error)
        this.$bvToast.toast('Erro ao carregar mensagens enviadas', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        this.mensagensEnviadas = []
      } finally {
        this.carregandoEnvios = false
      }
    },

    fecharModalEnvios() {
      this.modalEnviosVisivel = false
      this.mensagensEnviadas = []
      this.currentPageEnvios = 1
      this.filtroEnvios = ''
      this.filtroStatusEnvio = 'all'
    },

    async verInformacoesCliente(envio) {
      this.modalClienteVisivel = true
      this.carregandoCliente = true
      this.clienteSelecionado = null

      try {
        const infoCliente = await buscarInfoClienteAniversario(
          this.campanhaAtiva.id,
          this.lojaSelecionada,
          envio.lead_id,
        )
        // Mesclar dados do envio com informações do cliente
        this.clienteSelecionado = {
          ...envio,
          ...infoCliente,
        }
      } catch (error) {
        console.error('Erro ao carregar informações do cliente:', error)
        // Se falhar ao buscar detalhes, mostra pelo menos os dados básicos do envio
        this.clienteSelecionado = envio
      } finally {
        this.carregandoCliente = false
      }
    },

    fecharModalCliente() {
      this.modalClienteVisivel = false
      this.clienteSelecionado = null
    },

    baixarCSVEnvios() {
      if (!this.mensagensEnviadas || this.mensagensEnviadas.length === 0) {
        this.$bvToast.toast('Nenhuma mensagem para exportar', {
          title: 'Aviso',
          variant: 'warning',
          solid: true,
        })
        return
      }

      try {
        const headers = [
          'Data Envio',
          'Nome do Cliente',
          'Telefone',
          'Tipo Template',
          'Status',
          'Data Aniversário',
        ]

        const rows = this.mensagensEnviadas.map(msg => [
          this.formatarDataCompleta(msg.sent_at),
          msg.customer_name || 'N/A',
          msg.customer_phone || 'N/A',
          msg.template_type === 'recent' ? 'Recente' : 'Inativo',
          this.getStatusLabel(msg.status),
          this.formatarDataAniversario(msg.birthday),
        ])

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
        ].join('\n')

        const BOM = '\uFEFF'
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })

        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        const nomeCampanha = this.campanhaAtiva?.name || 'campanha-aniversario'
        const timestamp = new Date().toISOString().split('T')[0]
        const nomeArquivo = `envios_${nomeCampanha}_${timestamp}.csv`

        link.setAttribute('href', url)
        link.setAttribute('download', nomeArquivo)
        link.style.visibility = 'hidden'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        URL.revokeObjectURL(url)

        this.$bvToast.toast('CSV baixado com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error('Erro ao gerar CSV:', error)
        this.$bvToast.toast('Erro ao gerar arquivo CSV', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async salvarCampanha() {
      if (!this.formularioValido) {
        let mensagemErro = 'Preencha todos os campos obrigatórios'
        if (!this.templateSelecionado1) {
          mensagemErro = 'Selecione o Template 1 para compradores recentes'
        } else if (!this.templateSelecionado2) {
          mensagemErro = 'Selecione o Template 2 para compradores inativos'
        } else if (this.template1TemMidia && !this.validacaoMidia1) {
          mensagemErro = 'Faça o upload da mídia para o Template 1'
        } else if (this.template2TemMidia && !this.validacaoMidia2) {
          mensagemErro = 'Faça o upload da mídia para o Template 2'
        }

        this.$bvToast.toast(mensagemErro, {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.processando = true

      try {
        // A criação já ativa automaticamente a campanha e inicia o worker
        const campanhaCriada = await criarCampanhaAniversario(this.form, this.lojaSelecionada)

        // Upload de mídia para Template 1 se necessário
        if (this.template1TemMidia && this.imageFile1) {
          await uploadMediaCampanhaAniversario(
            campanhaCriada.id,
            this.imageFile1,
            this.lojaSelecionada,
            'recent', // Identificador do template
          )
        }

        // Upload de mídia para Template 2 se necessário
        if (this.template2TemMidia && this.imageFile2) {
          await uploadMediaCampanhaAniversario(
            campanhaCriada.id,
            this.imageFile2,
            this.lojaSelecionada,
            'inactive', // Identificador do template
          )
        }

        this.$bvToast.toast('Campanha de aniversário criada e ativada com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        })

        await this.verificarCampanhaAtiva()
      } catch (error) {
        console.error('Erro ao criar campanha:', error)

        let mensagemErro = 'Erro ao criar campanha de aniversário'
        if (error.response?.data?.error) {
          mensagemErro = error.response.data.error
        } else if (error.response?.data?.message) {
          mensagemErro = error.response.data.message
        }

        this.$bvToast.toast(mensagemErro, {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
        })
      } finally {
        this.processando = false
      }
    },

    confirmarDesativacao() {
      this.$bvModal.msgBoxConfirm(
        'Tem certeza que deseja desativar a campanha de aniversário? Os envios automáticos serão interrompidos.',
        {
          title: 'Confirmar Desativação',
          size: 'md',
          okVariant: 'danger',
          okTitle: 'Sim, desativar',
          cancelTitle: 'Cancelar',
          centered: true,
        },
      ).then(confirmado => {
        if (confirmado) {
          this.desativarCampanha()
        }
      })
    },

    async desativarCampanha() {
      this.processando = true

      try {
        await desativarCampanhaAniversario(this.campanhaAtiva.id, this.lojaSelecionada)

        this.$bvToast.toast('Campanha desativada com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        })

        this.campanhaAtiva = null
        this.metricas = null
      } catch (error) {
        console.error('Erro ao desativar campanha:', error)

        let mensagemErro = 'Erro ao desativar campanha'
        if (error.response?.data?.error) {
          mensagemErro = error.response.data.error
        }

        this.$bvToast.toast(mensagemErro, {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.processando = false
      }
    },

    formatarHorario(time) {
      if (!time) return '--:--'
      return time.substring(0, 5)
    },

    voltarParaLista() {
      this.$router.push({ name: 'campaign-ia' })
    },
  },
}
</script>

<style scoped>
.campanhas-aniversario-container {
  padding: 2rem;
  min-height: 100vh;
  background: #f8f9fa;
}

.page-header {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #5e5873;
  display: flex;
  align-items: center;
}

.breadcrumb-custom {
  background: transparent;
  padding: 0;
  margin: 0;
}

.loja-info {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.status-badge-active {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #28c76f 0%, #1f9d57 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.config-info {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.info-item {
  display: flex;
  align-items: center;
}

.metricas-section {
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.metrica-card {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.metrica-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.metrica-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 1rem;
}

.metrica-content h4 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #5e5873;
}

.aniversariantes-list {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

.aniversariante-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
  margin-bottom: 0.5rem;
}

.aniversariante-item:last-child {
  margin-bottom: 0;
}

.empty-icon {
  width: 120px;
  height: 120px;
  margin: 0 auto;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-area {
  cursor: pointer;
  text-align: center;
  background: #f8f9fa;
  transition: background 0.2s;
}

.upload-area:hover {
  background: #e9ecef;
}

.media-preview .img-preview,
.media-preview .video-preview {
  max-width: 100%;
  max-height: 280px;
  display: block;
}

.media-preview .video-preview {
  width: 100%;
}

.campanha-card,
.config-card {
  border-radius: 12px;
  border: none;
}

.info-box {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

/* Template Selector (mesma lógica da Recomendação de Produtos) */
.template-selector {
  margin-top: 0.5rem;
}

.template-empty {
  border: 2px dashed #d8d6de;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafafa;
}

.template-empty:hover {
  border-color: #9A3092;
  background: #f8f7ff;
}

.template-selected {
  border: 2px solid #28c76f;
  border-radius: 8px;
  padding: 1rem;
  background: #f0fff4;
}

/* Template Info Box */
.template-info-box {
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 8px;
}

.template-info-box ul {
  list-style-type: none;
  padding-left: 0;
}

.template-info-box ul li {
  padding: 0.25rem 0;
  padding-left: 1.5rem;
  position: relative;
}

.template-info-box ul li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #1976d2;
  font-weight: bold;
}

/* Seção de Conversões */
.conversoes-section {
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.conversao-card {
  border-left: 3px solid #9A3092;
}

.cursor-pointer {
  cursor: pointer;
}

/* Modal de Pedidos */
.pedidos-container {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.pedidos-table {
  font-size: 0.9rem;
  margin-bottom: 0;
}

.pedidos-table ::v-deep th {
  background-color: #f8f9fa;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  color: #6e6b7b;
  position: sticky;
  top: 0;
  z-index: 10;
}

.pedidos-table ::v-deep td {
  vertical-align: middle;
}

.pedidos-table ::v-deep tr:hover {
  background-color: #f8f9fa;
}

.pedidos-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.pedidos-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.pedidos-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.pedidos-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Card clicável */
.metrica-card-clickable {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.metrica-card-clickable:hover {
  border-color: #9A3092;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(154, 48, 146, 0.15);
}

/* Modal de Envios */
.envios-container {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.envios-table {
  font-size: 0.9rem;
  margin-bottom: 0;
}

.envios-table ::v-deep th {
  background-color: #f8f9fa;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  color: #6e6b7b;
  position: sticky;
  top: 0;
  z-index: 10;
}

.envios-table ::v-deep td {
  vertical-align: middle;
}

.envios-table ::v-deep tr:hover {
  background-color: #f8f9fa;
}

.envios-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.envios-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.envios-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.envios-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Modal de Cliente */
.cliente-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.cliente-avatar {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #9A3092 0%, #61185b 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.compras-list .compra-item {
  transition: background 0.2s ease;
}

.compras-list .compra-item:hover {
  background: #f8f9fa;
}

.gap-2 {
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .campanhas-aniversario-container {
    padding: 1rem;
  }

  .page-header {
    padding: 1.5rem;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .metrica-card {
    margin-bottom: 1rem;
  }

  .conversoes-section .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .conversoes-section .d-flex > div:last-child {
    margin-top: 0.5rem;
  }

  .cliente-header {
    padding: 1rem;
  }

  .cliente-avatar {
    width: 48px;
    height: 48px;
  }
}
</style>
